import {
  ChevronDownIcon,
  PlusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useCallback, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '@/components/ui/button';
import { Popover } from '@/components/Popover';
import { cn } from '@/utils/classname';

export interface RangeSelectorProps {
  value?: { min: string; max: string };
  onChange?: (value: { min: string; max: string }) => void;
  maxRange?: number;
  step?: number;
  hideSelectedRange?: boolean;
  rangeColors?: Record<number, string>;
}

export function RangeSelector({
  value,
  onChange,
  maxRange = 10,
  step = 1,
  hideSelectedRange = false,
  rangeColors,
}: RangeSelectorProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const minRef = useRef<HTMLDivElement>(null);
  const maxRef = useRef<HTMLDivElement>(null);
  const selectedRangeRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<'min' | 'max' | null>(null);

  const rangeColorsEntries = Object.entries(rangeColors || {});

  function formatValue(value: number): string {
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }

  function calculatePosition(clientX: number): number {
    if (!sliderRef.current) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    const scaledPosition =
      (Math.min(Math.max(position, 0), 100) / 100) * maxRange;
    return Math.round(scaledPosition / step) * step;
  }

  function handleMouseDown(e: React.MouseEvent, handle: 'min' | 'max') {
    isDragging.current = handle;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  function handleMouseMove(e: MouseEvent) {
    if (
      !isDragging.current ||
      !minRef.current ||
      !maxRef.current ||
      !selectedRangeRef.current
    )
      return;

    const value = calculatePosition(e.clientX);

    const currentMin =
      (parseInt(minRef.current.style.left || '0') / 100) * maxRange;
    const currentMax =
      (parseInt(maxRef.current.style.left || '100') / 100) * maxRange;

    if (isDragging.current === 'min') {
      const newMin = Number(formatValue(Math.min(value, currentMax)));

      if (newMin === currentMax) {
        return;
      }

      const newMinPercentage = (newMin / maxRange) * 100;
      minRef.current.style.left = `${newMinPercentage}%`;
      selectedRangeRef.current.style.left = `${newMinPercentage}%`;
      selectedRangeRef.current.style.width = `${(currentMax / maxRange) * 100 - newMinPercentage}%`;
      minRef.current.querySelector('#range')!.textContent = newMin.toString();
    } else if (isDragging.current === 'max') {
      const newMax = Number(formatValue(Math.max(value, currentMin)));

      if (newMax === currentMin) {
        return;
      }

      const newMaxPercentage = (newMax / maxRange) * 100;
      maxRef.current.style.left = `${newMaxPercentage}%`;
      selectedRangeRef.current.style.width = `${newMaxPercentage - (currentMin / maxRange) * 100}%`;
      maxRef.current.querySelector('#range')!.textContent = newMax.toString();
    }
  }

  function handleMouseUp() {
    if (!isDragging.current || !minRef.current || !maxRef.current) return;

    const minPercentage = parseInt(minRef.current.style.left || '0');
    const maxPercentage = parseInt(maxRef.current.style.left || '100');

    onChange?.({
      min: formatValue(
        Math.round(((minPercentage / 100) * maxRange) / step) * step
      ),
      max: formatValue(
        Math.round(((maxPercentage / 100) * maxRange) / step) * step
      ),
    });

    isDragging.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }

  return (
    <div
      className="h-10 pr-3 pb-2 pl-2"
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.closest('form')?.requestSubmit();
        }
      }}
      tabIndex={0}
    >
      <div className="flex size-full items-end">
        <div
          ref={sliderRef}
          className="bg-chariot-stroke relative flex h-1 w-full"
        >
          <div
            className={cn(
              'bg-chariot-brand-primary absolute z-10 h-1 rounded-full',
              hideSelectedRange && 'invisible'
            )}
            style={{
              left: `${(Number(value?.min || 0) / maxRange) * 100}%`,
              width: `${((Number(value?.max || maxRange) - Number(value?.min || 0)) / maxRange) * 100}%`,
            }}
            ref={selectedRangeRef}
          />
          {rangeColorsEntries.length > 0 && (
            <div className="absolute flex h-1 w-full">
              <div
                className={cn(
                  'bg-chariot-stroke absolute top-1/2 left-0 h-4 w-1 -translate-y-1/2 rounded',
                  rangeColorsEntries[0][1]
                )}
              />
              {rangeColorsEntries.map(([percentage, color], index) => (
                <div
                  key={index}
                  className={cn('h-full', `${color}`)}
                  style={{
                    width: `${index === 0 ? percentage : Number(percentage) - Number(rangeColorsEntries[index - 1][0])}%`,
                  }}
                />
              ))}
              <div
                className={cn(
                  'bg-chariot-stroke absolute top-1/2 right-0 h-4 w-1 -translate-y-1/2 rounded',
                  rangeColorsEntries[rangeColorsEntries.length - 1][1]
                )}
              />
            </div>
          )}
          <div
            className="absolute bottom-0 translate-y-1/2"
            style={{
              left: `${(Number(value?.min || 0) / maxRange) * 100}%`,
            }}
            ref={minRef}
          >
            <button
              className="border-chariot-background-primary bg-chariot-brand-primary relative -ml-2 size-5 cursor-pointer rounded-full border-2"
              onMouseDown={e => handleMouseDown(e, 'min')}
              type="button"
            >
              <span
                id="range"
                className="absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-bold"
              >
                {value?.min || 0}
              </span>
            </button>
          </div>
          <div
            className="absolute bottom-0 translate-y-1/2"
            style={{
              left: `${(Number(value?.max || maxRange) / maxRange) * 100}%`,
            }}
            ref={maxRef}
          >
            <button
              className="border-chariot-background-primary bg-chariot-brand-primary relative -ml-2 size-5 cursor-pointer rounded-full border-2"
              onMouseDown={e => handleMouseDown(e, 'max')}
              type="button"
            >
              <span
                id="range"
                className="absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-bold"
              >
                {value?.max || maxRange}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface RangeSelectorDropdownProps
  extends Omit<RangeSelectorProps, 'onChange'> {
  label: string;
  onApply?: (value: RangeSelectorProps['value']) => void;
}

export function RangeSelectorDropdown({
  value,
  label,
  onApply,
  ...rangeSelectorProps
}: RangeSelectorDropdownProps) {
  const [open, setOpenRaw] = useState(false);
  const [selectedRange, setSelectedRange] =
    useState<RangeSelectorDropdownProps['value']>(value);
  const isActive = value !== undefined;

  // Sync selectedRange when popover opens
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setSelectedRange(value);
      }
      setOpenRaw(nextOpen);
    },
    [value]
  );

  const selectedRangeLabel = value
    ? value.min === value.max
      ? value.min
      : `${value.min} - ${value.max}`
    : '';

  function handleClearClick(e: React.MouseEvent) {
    e.stopPropagation();
    setSelectedRange(undefined);
    onApply?.(undefined);
  }

  return (
    <Popover
      open={open}
      setOpen={setOpen}
      container={
        <button
          data-testid="dashed-dropdown"
          className={twMerge(
            'flex max-w-full min-w-0 items-center rounded-full border border-dashed px-2 py-1 text-xs font-bold transition-colors duration-150',
            isActive
              ? 'border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary'
              : 'border-chariot-stroke text-chariot-text-secondary'
          )}
        >
          {isActive ? (
            <XCircleIcon
              className="text-chariot-text-primary mr-2 size-5 shrink-0"
              onClick={handleClearClick}
            />
          ) : (
            <PlusCircleIcon className="text-chariot-text-secondary mr-2 size-5 shrink-0" />
          )}
          <span className="truncate uppercase">{label}</span>
          {isActive && (
            <>
              <span className="text-chariot-brand-primary mx-2">|</span>
              <span className="inline-block max-w-[200px] truncate">
                {selectedRangeLabel}
              </span>
              <ChevronDownIcon className="ml-2 size-4 shrink-0 text-current" />
            </>
          )}
        </button>
      }
    >
      <form
        className="flex w-64 flex-col gap-3"
        onSubmit={event => {
          event.preventDefault();

          onApply?.(
            selectedRange || {
              min: '0',
              max: (rangeSelectorProps.maxRange || 10).toString(),
            }
          );
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">
            Filter by {label.toUpperCase()} Score between...
          </span>
        </div>
        {
          <RangeSelector
            value={selectedRange}
            onChange={setSelectedRange}
            {...rangeSelectorProps}
          />
        }
        <Button
          type="submit"
          size="sm"
          className="mt-2"
          onClick={() => {}}
        >Apply</Button>
      </form>
    </Popover>
  );
}
